import React, { useContext, useEffect, useState } from 'react';
import { useSelector, ReactReduxContext } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import TallyForm from '../../components/TallyForm';
import Spinner from '../../components/Spinner';
import Box from '../../components/shared/atoms/Box';
import Text from '../../components/shared/atoms/Text';
import AutodiagFlowChoice from '../../components/repair/AutodiagFlowChoice';
import AutodiagFrame from '../../components/AutodiagFrame';
import { setBookingUUID } from '../../actions/course';
import { dumpStore } from '../../utils/reparation';
import {
  buildAutodiagStartUrl,
  isAutodiagEmbedEnabled,
  isAutodiagLlmEnabled,
  resolveTunnelBookingUuid,
} from '../../utils/autodiag';
import {
  sendAutodiagExposure,
  EXPOSURE_EVENTS,
  FLOW_VALUES,
} from '../../utils/autodiagExposure';

const TransitionScreen = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  text-align: center;
`;

// Étape auto-diagnostic du tunnel (/reparation/autodiag) — ticket FIX-429.
// Machine à trois états, aucune redirection automatique :
//   choice === null   → écran de choix à deux cartes (assistant / formulaire)
//   choice === 'llm'  → écran de transition + redirection pleine page vers le
//                       service LLM, qui nous renverra sur /reparation/date
//                       avec booking_uuid + capabilities
//   choice === 'tally'→ TallyForm rendu dans l'étape, avec le MÊME bookingUuid
//                       en prop (jointure booking_uuid oblige)
// Événements d'exposition émis : choice_viewed au montage de l'écran de choix,
// flow_chosen ('llm' | 'tally') au clic sur une carte.
// ROLLBACK : flag AUTODIAG_LLM_ENABLED dans src/utils/autodiag.jsx (ou
// ?autodiag_llm=0 dans l'URL) → Tally direct, sans écran de choix et sans
// AUCUN événement. La garde anti-double-envoi vit dans
// src/utils/autodiagExposure.js (une émission par (event, booking_uuid)).
function RepairAutodiag(props) {
  const { store } = useContext(ReactReduxContext);
  const { t } = useTranslation();
  const position = useSelector((s) => s.course.position);
  // Couverture réelle de la zone (alimentée par GET /zones/availability à
  // l'étape adresse). Test strict === true : null (pas d'adresse saisie)
  // et false (hors zone) masquent le badge « Adresse couverte ».
  const isInZone = useSelector((s) => s.course.isInZone);
  const search = props.location ? props.location.search : '';
  // Figé au montage : pendant la transition de sortie (CSSTransition ~1 s),
  // la scène reçoit la nouvelle location — un search qui se vide ne doit pas
  // faire basculer un utilisateur rollback (contractuellement zéro événement)
  // vers l'écran de choix et déclencher un choice_viewed.
  const [llmEnabled] = useState(() => isAutodiagLlmEnabled(search));
  // Figé au montage, même raison que llmEnabled. Embed (AutodiagFrame,
  // contrat AD-45) par défaut ; ?autodiag_embed=0 → redirection pleine page
  // vers le service (comportement FIX-429).
  const [embedEnabled] = useState(() => isAutodiagEmbedEnabled(search));
  // UUID stable pour tout le passage de tunnel dans cet onglet (slot
  // sessionStorage, cf. resolveTunnelBookingUuid) : un remontage de la scène
  // (retour depuis /reparation/date) réutilise le même uuid — sans ça, la
  // dédup analytics (event, uuid) est inopérante.
  const [bookingUuid] = useState(() =>
    resolveTunnelBookingUuid(
      store.getState().course && store.getState().course.booking_uuid,
    ),
  );
  const [choice, setChoice] = useState(null);

  // Persistance de l'UUID — les deux bras, rollback compris : l'UUID doit être
  // dans localStorage['state'] avant toute navigation ou tout rendu de Tally,
  // pour être envoyé au POST /courses du checkout.
  useEffect(() => {
    // Le subscribe() posé par configureStore appelle dumpStore de façon
    // synchrone à chaque dispatch → localStorage['state'] contient le
    // booking_uuid immédiatement.
    store.dispatch(setBookingUUID(bookingUuid));

    // Ceinture et bretelles : si la persistance n'a pas eu lieu (subscribe
    // absent, écriture avortée…), on force l'écriture.
    try {
      const persisted = JSON.parse(localStorage.getItem('state') || '{}');
      if (!persisted.course || persisted.course.booking_uuid !== bookingUuid) {
        dumpStore(store.getState());
      }
    } catch (e) {
      dumpStore(store.getState());
    }
  }, [bookingUuid, store]);

  // bfcache : après clic « Assistant » + navigation, le bouton retour restaure
  // la page avec choice === 'llm' → spinner sans issue. pageshow persisted
  // ramène l'écran de choix (aucun event ré-émis : la dédup tient).
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setChoice(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // choice_viewed au montage de l'écran de choix — jamais sur le chemin
  // rollback. Idempotent au remontage grâce à la garde du module.
  useEffect(() => {
    if (!llmEnabled) return;
    sendAutodiagExposure({
      bookingUuid,
      event: EXPOSURE_EVENTS.CHOICE_VIEWED,
    });
  }, [llmEnabled, bookingUuid]);

  const handlePickLlm = () => {
    // Tir-et-oublie (fetch keepalive) : la navigation n'attend pas l'event.
    sendAutodiagExposure({
      bookingUuid,
      event: EXPOSURE_EVENTS.FLOW_CHOSEN,
      value: FLOW_VALUES.LLM,
    });
    setChoice('llm');
    // Embed : AutodiagFrame (rendu ci-dessous) prend le relais — pas de
    // navigation, l'assistant s'ouvre en iframe dans l'étape.
    if (embedEnabled) return;
    const returnUrl = `${window.location.origin}/reparation/date?autodiag=v2`;
    window.location.assign(
      buildAutodiagStartUrl({ bookingUuid, returnUrl, position }),
    );
  };

  const handlePickTally = () => {
    sendAutodiagExposure({
      bookingUuid,
      event: EXPOSURE_EVENTS.FLOW_CHOSEN,
      value: FLOW_VALUES.TALLY,
    });
    setChoice('tally');
  };

  // Rollback : Tally direct, AUCUN événement. L'UUID reste passé en prop
  // (source unique de la clé de jointure), il n'émet rien.
  if (!llmEnabled) {
    return (
      <div>
        <TallyForm {...props} bookingUuid={bookingUuid} />
      </div>
    );
  }

  if (choice === 'llm') {
    // Embed : l'assistant vit dans l'étape ; « Quitter » côté service
    // (autodiag_status=cancelled) ramène à l'écran de choix.
    if (embedEnabled) {
      return <AutodiagFrame onCancel={() => setChoice(null)} />;
    }
    return (
      <TransitionScreen>
        <Spinner />
        <Text mt={4}>{t('autodiag.choice.loading')}</Text>
      </TransitionScreen>
    );
  }

  if (choice === 'tally') {
    return (
      <div>
        <TallyForm {...props} bookingUuid={bookingUuid} />
      </div>
    );
  }

  return (
    <AutodiagFlowChoice
      addressCovered={isInZone === true}
      onPickLlm={handlePickLlm}
      onPickTally={handlePickTally}
    />
  );
}

export default RepairAutodiag;
